<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import AddLabel from 'dashboard/components-next/label/AddLabel.vue';
import LabelItem from 'dashboard/components-next/label/LabelItem.vue';
import LeadsAPI from 'dashboard/api/leads';

// Form de contacto NUEVO para el alta de lead: contacto nativo + cargo (atributos
// personalizados) + etiquetas + una nota. Expone createContact(): crea el Contacto
// nativo con cargo, le aplica etiquetas y nota, y devuelve el contacto.
const store = useStore();
const { t } = useI18n();

const contactsFormRef = ref(null);
const customAttrValues = ref({});
const selectedLabels = ref([]); // [{ id, title, color }]
const hoveredLabel = ref(null);
const note = ref('');

// Definiciones de atributos personalizados de contacto (cargo y cualquier otro).
const customAttributeDefs = computed(
  () => store.getters['attributes/getContactAttributes'] || []
);
const allLabels = computed(() => store.getters['labels/getLabels'] || []);

const labelMenuItems = computed(() =>
  allLabels.value
    .map(label => ({
      label: label.title,
      value: label.id,
      thumbnail: { name: label.title, color: label.color },
      isSelected: selectedLabels.value.some(s => s.id === label.id),
      action: 'label',
    }))
    .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected))
);

onMounted(() => {
  store.dispatch('attributes/get');
  store.dispatch('labels/get');
});

const toggleLabel = ({ value }) => {
  const label = allLabels.value.find(l => l.id === value);
  if (!label) return;
  const idx = selectedLabels.value.findIndex(s => s.id === value);
  if (idx >= 0) selectedLabels.value.splice(idx, 1);
  else selectedLabels.value.push(label);
};

const removeLabel = label => {
  selectedLabels.value = selectedLabels.value.filter(s => s.id !== label.id);
};

// Crea el contacto nativo (base + cargo). Si falla → lanza (el alta aborta sin
// dejar lead huérfano). Luego aplica etiquetas y nota: si ALGUNA falla, el
// contacto+lead igual quedan creados y se marca `partial` para avisar al usuario
// (las puede corregir en la ficha). Devuelve { contact, partial }.
// Busca el dueño (agente + desde cuándo) del contacto que matchea el teléfono/email
// ingresado. Best-effort: si falla, devuelve null y la ficha cae al mensaje genérico.
const fetchDuplicateOwner = async state => {
  try {
    const { data } = await LeadsAPI.duplicateOwner({
      phone: state.phoneNumber,
      email: state.email,
    });
    return data;
  } catch (error) {
    return null;
  }
};

const createContact = async () => {
  const state = contactsFormRef.value?.state || {};
  const { firstName, lastName, ...rest } = state;
  const name =
    (state.name && state.name.trim()) ||
    `${firstName || ''} ${lastName || ''}`.trim();
  if (!name) return { contact: null, partial: false };

  let contact;
  try {
    contact = await store.dispatch('contacts/create', {
      ...rest,
      name,
      customAttributes: customAttrValues.value,
    });
  } catch (error) {
    // Contacto duplicado → enriquecer la excepción con el dueño (agente + desde cuándo)
    // para que la ficha muestre el modal "Lead Duplicado". El lookup es best-effort.
    if (error?.name === 'DuplicateContactException') {
      error.data = await fetchDuplicateOwner(state);
    }
    throw error;
  }
  if (!contact?.id) return { contact: null, partial: false };

  let partial = false;
  if (selectedLabels.value.length) {
    try {
      await store.dispatch('contactLabels/update', {
        contactId: contact.id,
        labels: selectedLabels.value.map(l => l.title),
      });
    } catch (error) {
      partial = true;
    }
  }
  if (note.value.trim()) {
    try {
      await store.dispatch('contactNotes/create', {
        contactId: contact.id,
        content: note.value.trim(),
      });
    } catch (error) {
      partial = true;
    }
  }
  return { contact, partial };
};

defineExpose({ createContact });
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-semibold text-n-slate-12">
      {{ t('LEADS.CONTACT.TITLE') }}
    </h3>

    <!-- Contacto nativo: nombre/apellido, email, teléfono, empresa, ciudad, país, redes -->
    <ContactsForm ref="contactsFormRef" :contact-data="{}" is-new-contact />

    <!-- Cargo y demás atributos personalizados del contacto -->
    <div v-if="customAttributeDefs.length" class="flex flex-col gap-3">
      <Input
        v-for="def in customAttributeDefs"
        :key="def.attributeKey"
        v-model="customAttrValues[def.attributeKey]"
        :label="def.attributeDisplayName"
      />
    </div>

    <!-- Etiquetas -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('LEADS.CONTACT.TAGS') }}
      </span>
      <div
        class="flex flex-wrap items-center gap-1"
        @mouseleave="hoveredLabel = null"
      >
        <LabelItem
          v-for="label in selectedLabels"
          :key="label.id"
          :label="label"
          :is-hovered="hoveredLabel === label.id"
          @remove="removeLabel"
          @hover="hoveredLabel = $event"
        />
        <AddLabel
          :label-menu-items="labelMenuItems"
          @update-label="toggleLabel"
        />
      </div>
    </div>

    <!-- Nota -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('LEADS.CONTACT.NOTES') }}
      </span>
      <textarea
        v-model="note"
        rows="2"
        :placeholder="t('LEADS.CONTACT.NOTE_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm border rounded-lg resize-none bg-n-background border-n-weak text-n-slate-12 focus:outline-none focus:border-n-slate-6"
      />
    </div>
  </div>
</template>
